import { Container, Fade, Link, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, useMediaQuery, useTheme } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ReactElement } from "react";
import { HttpClient } from "../api/HttpClient";
import { RecentRoomsResponse, PeriodComparisonResponse } from "../api/Requests";
import { AuthState, UserContext } from "../contexts/UserContext";
import { Action, Category } from "../analytics/Tracking";
import { useJoinRoom } from "../hooks/useRoom";
import { useTracking } from "../analytics/useTracking";
import { TrendIcon, TrendDirection } from "../components/TrendIcon";

interface RecentRoomsProps {
  onAlignmentTrendClicked?: (roomId: string) => void;
}

export default function RecentRooms({ onAlignmentTrendClicked }: RecentRoomsProps): ReactElement {
  const { track } = useTracking();
  const { authState, authToken } = useContext(UserContext);
  const [, setIsLoading] = useState(false);
  const [response, setResponse] = useState<RecentRoomsResponse>();
  const [trends, setTrends] = useState<Record<string, PeriodComparisonResponse | null>>({});
  const isFetchingRef = useRef(false);

  const theme = useTheme();
  const isScreenSizeMobile = useMediaQuery(theme.breakpoints.up('sm'));
  const [joinRoom] = useJoinRoom();

  useEffect(() => {
    if (authState !== AuthState.SignedIn) {
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const httpClient = new HttpClient(authToken);
        const r = (await httpClient.get("/rooms/recent?includeTrends=true")) as RecentRoomsResponse;

        setResponse(r);
        
        // Extract trends from response (now included in the API response)
        if (r?.recentRoomVisits) {
          const trendsMap: Record<string, PeriodComparisonResponse | null> = {};
          r.recentRoomVisits.forEach((room) => {
            trendsMap[room.roomUrlId] = room.trend || null;
          });
          setTrends(trendsMap);
        }
        
        setIsLoading(false);
      } catch {
        setIsLoading(false);
      } finally {
        isFetchingRef.current = false;
      }
    };

    fetchData();
  }, [authState, authToken]);

  const roomLink = (roomUrlId: string) => {
     const preFix = isScreenSizeMobile ? window.location.origin + "/" : "";
    return  preFix + roomUrlId;
  }

  const hasAnyTrends = () => {
    if (!response?.recentRoomVisits) return false;
    return response.recentRoomVisits.some(room => {
      const trend = trends[room.roomUrlId];
      return trend && 
             trend.estimationRoundsCurrentPeriod >= 2 && 
             trend.averageScorePreviousPeriod && 
             trend.averageScorePreviousPeriod > 0;
    });
  };

  const renderTrend = (roomUrlId: string) => {
    const trend = trends[roomUrlId];
    if (!trend || trend.estimationRoundsCurrentPeriod < 2 || !trend.averageScorePreviousPeriod || trend.averageScorePreviousPeriod <= 0) {
      return null;
    }

    return (
      <TrendIcon
        direction={trend.averageScoreTrendDirection as TrendDirection}
        percentageChange={trend.averageScorePercentageChange}
        onClick={(e) => {
          e.stopPropagation();
          if (onAlignmentTrendClicked) {
            onAlignmentTrendClicked(roomUrlId);
          }
        }}
        size="small"
        tooltipSuffix=" vs previous 30 days"
      />
    );
  };

  return (
    <Fade in={true}>
    <Container maxWidth="md">
      <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Recent Rooms</TableCell>
            <TableCell align="right">Players</TableCell>
            <TableCell align="right">Last Visited</TableCell>
            {isScreenSizeMobile && hasAnyTrends() && <TableCell align="right">Alignment Trend</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {response?.recentRoomVisits.map((row) => (
            <TableRow key={row.roomUrlId}>
              <TableCell component="th" scope="row">
                <Link color="inherit" sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }} href={window.location.origin + "/" + row.roomUrlId} variant="body1" onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  track.event(Category.User, Action.RecentRoomVisited);
                  joinRoom(row.roomUrlId, row.player.id, row.player.name, row.player.isModerator)
                  }}>
                  {roomLink(row.roomUrlId)}
              </Link>
              </TableCell>
              <TableCell align="right">{row.playerNames}</TableCell>
              <TableCell align="right">{row.daysSinceLastVisit} {row.daysSinceLastVisit == 1 ? "day" : "days"} ago</TableCell>
              {isScreenSizeMobile && hasAnyTrends() && <TableCell align="right">{renderTrend(row.roomUrlId)}</TableCell>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    </Container>
    </Fade>
  );
}